import axios, { AxiosError } from "axios";
import { useContext } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import { ADMIN_API_URL } from "../../../utils/env";

// One withdrawal's contribution to the kill-switch counter in a window. Value
// is the USD counted toward the cap (once per unique id); submissions is how
// many times that id re-entered the stream — submissions > 1 means the same
// withdrawal was resubmitted and would have inflated the counter pre-dedup.
export interface IKillSwitchUsageEntry {
  id: string;
  account: string;
  collateral: string;
  amount: string;
  value: string;
  submissions: number;
  first_seen: string;
  last_seen: string;
}

export interface IKillSwitchWindowArchive {
  reset_at: string;
  total: string;
  tripped: boolean;
  entries: IKillSwitchUsageEntry[];
}

export interface IKillSwitchUsage {
  tripped: boolean;
  threshold: string;
  used: string;
  current: IKillSwitchUsageEntry[];
  history: IKillSwitchWindowArchive[];
}

// GET /daily-kill-switch-usage on the exchange-backend admin server. Returns
// the per-withdrawal breakdown that sums to the current counter (so an admin
// can tell "$190k = 19 × $10k retried" from "one $190k withdrawal") plus the
// archived ledgers of prior windows (pruned to ~2 days backend-side).
//
// Fetched unconditionally (30s poll) so the panel's collapsed header — the
// withdrawal count and "sums to" total — is always accurate, not just after
// the panel is expanded. The payload is small (one row per unique withdrawal
// in the window).
export const useKillSwitchUsage = () => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const endpoint = ADMIN_API_URL(AdminEndpointEnum.DAILY_KILL_SWITCH_USAGE);

  return useSWR<IKillSwitchUsage, AxiosError>(
    tokenId ? [endpoint, tokenId] : undefined,
    async () => {
      const response = await axios.get<IKillSwitchUsage>(
        endpoint,
        queryConfig()
      );
      return response.data;
    },
    { refreshInterval: 30000 }
  );
};
